// 012A/012B — Imagenología (Fase 7, Tanda 3, ver PLAN.md). Transcripción campo por campo del PDF oficial del
// Anexo 1 MSP: 012A hoja idx 51 y 012B hoja idx 55 (footer `SNS-MSP/HCU-form.012A|012B/2021`, 1 hoja cada una).
// Informe de brechas en `docs/hce-fase7/brechas-010-012-013.md`.
//
// Lo que el papel NO trae y se retiró de la UI (estaba inventado): "condiciones de traslado" (012A) y el
// sub-bloque "eco obstétrica" (012B). Los recursos ya guardados en Medplum no se tocan.
//
// FHIR (consumidores reales: `LabsTab`, `SubirDocumento`, `SelectorRecurso`): 012A sigue creando un
// `ServiceRequest` (status `active`, intent `order`, categoría imagenología, `code.text` legible, `priority`,
// `reasonCode` por diagnóstico); 012B un `DiagnosticReport` cuyo `conclusion` lleva los hallazgos (+ las
// conclusiones y sugerencias). Todo lo demás va como `Par[]` de respaldo en el PDF firmado.
import { diagnosticoConMarcaVacio, textoDiagnosticoConMarca, type DiagnosticoConMarca } from './MarcaDiagnostico';
import {
  MARCAS_PRE_DEF,
  OPCIONES_SI_NO,
  paresDeCampos,
  valoresVacios,
  type CampoSimple,
  type OpcionCampo,
  type Valores,
} from './camposSimplesDatos';
import type { Par } from './index';

const casilla = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'casilla' });
const opciones = (...etiquetas: readonly string[]): readonly OpcionCampo[] => etiquetas.map((e) => ({ valor: e, etiqueta: e }));

const OPCIONES_SERVICIO = opciones('Emergencia', 'Consulta externa', 'Hospitalización');

/** Bloque "Estudio de imagenología solicitado/realizado": mismas casillas en 012A (C) y 012B (C). */
export const CAMPOS_ESTUDIO_IMAGEN: readonly CampoSimple[] = [
  casilla('rxConvencional', 'RX convencional'),
  casilla('rxPortatil', 'RX portátil'),
  casilla('tomografia', 'Tomografía'),
  casilla('resonancia', 'Resonancia'),
  casilla('ecografia', 'Ecografía'),
  casilla('mamografia', 'Mamografía'),
  casilla('procedimiento', 'Procedimiento'),
  casilla('otro', 'Otro'),
  { clave: 'sedacion', etiqueta: 'Sedación', tipo: 'select', opciones: OPCIONES_SI_NO },
  { clave: 'descripcion', etiqueta: 'Descripción', tipo: 'area' },
];

const CLAVES_ESTUDIO = ['rxConvencional', 'rxPortatil', 'tomografia', 'resonancia', 'ecografia', 'mamografia', 'procedimiento', 'otro'] as const;

/** Etiquetas de los estudios marcados, en el orden impreso. */
export function estudiosMarcados(estudio: Valores): string[] {
  return CLAVES_ESTUDIO.filter((c) => estudio[c] === 'true').map((c) => CAMPOS_ESTUDIO_IMAGEN.find((x) => x.clave === c)!.etiqueta);
}

// ---------- 012A ----------

export const CAMPOS_SERVICIO_012A: readonly CampoSimple[] = [
  { clave: 'servicio', etiqueta: 'Servicio', tipo: 'select', opciones: OPCIONES_SERVICIO },
  { clave: 'especialidad', etiqueta: 'Especialidad' },
  { clave: 'cama', etiqueta: 'Cama' },
  { clave: 'sala', etiqueta: 'Sala' },
  { clave: 'prioridad', etiqueta: 'Prioridad', tipo: 'select', opciones: opciones('Urgente', 'Rutina', 'Control') },
];

export const CAMPOS_MOTIVO_012A: readonly CampoSimple[] = [
  { clave: 'fum', etiqueta: 'FUM', tipo: 'fecha' },
  { clave: 'pacienteContaminado', etiqueta: 'Paciente contaminado', tipo: 'select', opciones: OPCIONES_SI_NO },
  { clave: 'motivo', etiqueta: 'Motivo de la solicitud (razones para solicitar el estudio)', tipo: 'area' },
];

export const CAMPOS_RESUMEN_012A: readonly CampoSimple[] = [{ clave: 'resumenClinico', etiqueta: 'Resumen clínico actual', tipo: 'area' }];

export const NUM_DIAGNOSTICOS_012A = 6;

export interface Estado012A {
  readonly servicio: Valores;
  readonly estudio: Valores;
  readonly motivo: Valores;
  readonly resumen: Valores;
  readonly diagnosticos: readonly DiagnosticoConMarca[];
}

export function estadoInicial012A(): Estado012A {
  return {
    servicio: valoresVacios(CAMPOS_SERVICIO_012A),
    estudio: valoresVacios(CAMPOS_ESTUDIO_IMAGEN),
    motivo: valoresVacios(CAMPOS_MOTIVO_012A),
    resumen: valoresVacios(CAMPOS_RESUMEN_012A),
    diagnosticos: Array.from({ length: NUM_DIAGNOSTICOS_012A }, () => diagnosticoConMarcaVacio()),
  };
}

/** Prioridad de FHIR: el papel imprime Urgente / Rutina / Control (Control no tiene equivalente y va como `routine`, como antes). */
export function prioridadFhir012A(prioridad: string | undefined): 'urgent' | 'routine' {
  return prioridad === 'Urgente' ? 'urgent' : 'routine';
}

/** `ServiceRequest.code.text`: los estudios marcados y el motivo — `LabsTab` lo muestra tal cual. */
export function textoSolicitud012A(e: Estado012A): string {
  const estudios = estudiosMarcados(e.estudio).join(', ') || 'Imagenología';
  const motivo = (e.motivo.motivo ?? '').trim();
  return motivo ? `${estudios}: ${motivo}` : estudios;
}

const paresDiagnosticos = (titulo: string, dx: readonly DiagnosticoConMarca[]): Par[] =>
  dx.map((d, i) => ({
    pregunta: `${titulo} ${i + 1}`,
    respuesta: textoDiagnosticoConMarca(d, MARCAS_PRE_DEF),
  }));

export function paresDe012A(e: Estado012A): Par[] {
  return [
    ...paresDeCampos(CAMPOS_SERVICIO_012A, e.servicio),
    ...paresDeCampos(CAMPOS_ESTUDIO_IMAGEN, e.estudio),
    ...paresDeCampos(CAMPOS_MOTIVO_012A, e.motivo),
    ...paresDeCampos(CAMPOS_RESUMEN_012A, e.resumen),
    ...paresDiagnosticos('Diagnóstico', e.diagnosticos),
  ].filter((p) => p.respuesta.trim() !== '');
}

// ---------- 012B ----------

export const CAMPOS_SERVICIO_012B: readonly CampoSimple[] = [
  { clave: 'profesionalRealiza', etiqueta: 'Profesional que realiza el estudio' },
  { clave: 'fechaRealizacion', etiqueta: 'Fecha de realización', tipo: 'fecha' },
  { clave: 'profesionalSolicitante', etiqueta: 'Profesional solicitante' },
  { clave: 'servicio', etiqueta: 'Servicio', tipo: 'select', opciones: OPCIONES_SERVICIO },
  { clave: 'especialidad', etiqueta: 'Especialidad' },
];

export const CAMPOS_HALLAZGOS_012B: readonly CampoSimple[] = [{ clave: 'hallazgos', etiqueta: 'Hallazgos por imagenología', tipo: 'area' }];
export const CAMPOS_CONCLUSIONES_012B: readonly CampoSimple[] = [{ clave: 'conclusiones', etiqueta: 'Conclusiones y sugerencias', tipo: 'area' }];

/** Leyenda impresa al pie del papel (012B), se muestra en pantalla como referencia. */
export const LEYENDA_012B =
  'La aproximación diagnóstica emitida en el presente informe, constituye tan solo una prueba complementaria al diagnóstico clínico definitivo, motivo por el cual se recomienda correlacionar con antecedentes clínicos/quirúrgicos, datos clínicos, exámenes de laboratorio complementarios, así como seguimiento imagenológico del paciente.';

export interface Estado012B {
  readonly servicio: Valores;
  readonly estudio: Valores;
  readonly hallazgos: Valores;
  readonly conclusiones: Valores;
}

export function estadoInicial012B(): Estado012B {
  return {
    servicio: valoresVacios(CAMPOS_SERVICIO_012B),
    estudio: valoresVacios(CAMPOS_ESTUDIO_IMAGEN),
    hallazgos: valoresVacios(CAMPOS_HALLAZGOS_012B),
    conclusiones: valoresVacios(CAMPOS_CONCLUSIONES_012B),
  };
}

/** `DiagnosticReport.conclusion` (lo lee `LabsTab`): los hallazgos y, debajo, las conclusiones y sugerencias. */
export function conclusionInforme012B(e: Estado012B): string | undefined {
  const hallazgos = (e.hallazgos.hallazgos ?? '').trim();
  const conclusiones = (e.conclusiones.conclusiones ?? '').trim();
  const texto = [hallazgos, conclusiones ? `Conclusiones y sugerencias: ${conclusiones}` : ''].filter(Boolean).join('\n\n');
  return texto || undefined;
}

export function paresDe012B(e: Estado012B): Par[] {
  return [
    ...paresDeCampos(CAMPOS_SERVICIO_012B, e.servicio),
    ...paresDeCampos(CAMPOS_ESTUDIO_IMAGEN, e.estudio),
    ...paresDeCampos(CAMPOS_HALLAZGOS_012B, e.hallazgos),
    ...paresDeCampos(CAMPOS_CONCLUSIONES_012B, e.conclusiones),
  ].filter((p) => p.respuesta.trim() !== '');
}
